// Anchored popover menu (kebab / overflow actions). Portal-rendered and fixed-positioned off the
// trigger's rect, so it is never clipped by a scrolling list — the reason it isn't just an
// absolutely-positioned div. Closes on Escape, outside pointerdown, scroll, resize, and after any
// item activation; focus returns to the trigger on close. Flips above the trigger when there isn't
// room below. Portal-agnostic — shared across apps.
// Style contract: .cc-menu / .cc-menu__item live in styles.css.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const MENU_MARGIN = 6;
const VIEWPORT_PAD = 8;

const MenuCloseContext = createContext<(refocus?: boolean) => void>(() => {});

export function Menu({
  trigger,
  children,
  label = "More actions",
  align = "end",
  width = 200,
  className,
}: {
  /** Rendered inside the trigger button (an icon, usually). */
  trigger: ReactNode;
  /** MenuItem children. */
  children: ReactNode;
  /** Accessible name for the trigger. */
  label?: string;
  /** Horizontal alignment of the popover against the trigger. */
  align?: "start" | "end";
  width?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setPos(null);
    if (refocus) btnRef.current?.focus();
  }, []);

  // Position against the trigger's viewport rect; flip up when the menu would overflow the bottom.
  useLayoutEffect(() => {
    if (!open) return;
    const anchor = btnRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const height = menu?.height ?? 0;
    const below = anchor.bottom + MENU_MARGIN;
    const flip = height > 0 && below + height > window.innerHeight - VIEWPORT_PAD;
    const top = flip ? Math.max(VIEWPORT_PAD, anchor.top - MENU_MARGIN - height) : below;
    const rawLeft = align === "end" ? anchor.right - width : anchor.left;
    const left = Math.min(Math.max(VIEWPORT_PAD, rawLeft), window.innerWidth - width - VIEWPORT_PAD);
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [open, align, width]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !btnRef.current?.contains(t)) close(false);
    };
    // A scroll/resize would leave the popover detached from its row — close instead of chasing it.
    const onReflow = () => close(false);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, close]);

  // Focus the first item once positioned, so keyboard users land inside the menu.
  useEffect(() => {
    if (open && pos) menuRef.current?.querySelector<HTMLElement>("[role='menuitem']")?.focus();
  }, [open, pos]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']") ?? []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`cc-menu-btn ${open ? "is-open" : ""} ${className ?? ""}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            className="cc-menu"
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width,
              visibility: pos ? "visible" : "hidden",
            }}
            onKeyDown={onMenuKeyDown}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuCloseContext.Provider value={close}>{children}</MenuCloseContext.Provider>
          </div>,
          document.body,
        )}
    </>
  );
}

/** One action row. Activating it runs `onSelect` and closes the menu. */
export function MenuItem({
  icon,
  children,
  onSelect,
  destructive,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
}) {
  const close = useContext(MenuCloseContext);
  return (
    <button
      type="button"
      role="menuitem"
      className={`cc-menu__item ${destructive ? "is-destructive" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        close(false);
        onSelect();
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
